import { db, nowIso, putAndQueue, type FoodRow } from '@/lib/db';
import type { Food, NutrientKey, NutrientSources, NutrientValues } from '@/engine/types';

/** Formato compacto del archivo generado: valores en columnas, no en objetos. */
interface PackedFood {
  i: string;
  n: string;
  s: string[];
  t: string[];
  v: (number | null)[];
  p: [string, number][];
}

interface Dataset {
  version: number;
  generated: string;
  source: string;
  nutrientKeys: NutrientKey[];
  foods: PackedFood[];
}

const DATASET_VERSION = 2;
const DATASET_URL = `/data/foods-v${DATASET_VERSION}.json`;
const META_KEY = 'catalogo:version';

export interface CatalogStatus {
  state: 'listo' | 'cargando' | 'error';
  loaded: number;
  total: number;
}

function unpack(packed: PackedFood, keys: NutrientKey[]): FoodRow {
  const per100g: NutrientValues = {};
  const sources: NutrientSources = {};

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]!;
    const value = packed.v[i] ?? null;
    per100g[key] = value;
    // La procedencia solo tiene sentido donde hay dato: lo desconocido no
    // viene "de USDA", simplemente no viene.
    if (value !== null) sources[key] = 'usda';
  }

  const food: Food = {
    id: packed.i,
    kind: 'generico',
    name: packed.n,
    synonyms: packed.s,
    per100g,
    sources,
    householdPortions: packed.p.map(([label, grams]) => ({ label, grams })),
    tags: packed.t,
  };

  return { ...food, updatedAt: nowIso(), deletedAt: null };
}

/**
 * Carga el catálogo en IndexedDB la primera vez.
 *
 * Se baja con fetch y se vuelca a Dexie en vez de precachearlo con el service
 * worker: guardarlo en Cache Storage además de en IndexedDB sería tener los
 * mismos datos dos veces. Ver DECISIONS.md D-24.
 */
export async function ensureCatalog(onProgress?: (status: CatalogStatus) => void): Promise<void> {
  // Primero se mira lo que YA hay en local y solo se va a la red si hace falta.
  // Preguntar siempre a la red rompía la app sin conexión aunque el catálogo
  // estuviera entero en IndexedDB, que es justo el caso para el que existe.
  const meta = await db.meta.get(META_KEY);
  const installed = typeof meta?.value === 'number' ? meta.value : 0;

  if (installed === DATASET_VERSION) {
    const count = await db.foods.where('kind').equals('generico').count();
    if (count > 0) {
      onProgress?.({ state: 'listo', loaded: count, total: count });
      return;
    }
  }

  const response = await fetch(DATASET_URL, { cache: 'force-cache' });
  if (!response.ok) throw new Error(`No se pudo cargar el catálogo (${response.status})`);

  const dataset = (await response.json()) as Dataset;

  const total = dataset.foods.length;
  onProgress?.({ state: 'cargando', loaded: 0, total });

  // Por lotes para no bloquear el hilo principal con 1.600 escrituras seguidas.
  const BATCH = 250;
  for (let i = 0; i < total; i += BATCH) {
    const rows = dataset.foods.slice(i, i + BATCH).map((f) => unpack(f, dataset.nutrientKeys));
    await db.foods.bulkPut(rows);
    onProgress?.({ state: 'cargando', loaded: Math.min(i + BATCH, total), total });
  }

  await db.meta.put({ key: META_KEY, value: dataset.version });
  await db.meta.put({ key: 'catalogo:fuente', value: dataset.source });
  onProgress?.({ state: 'listo', loaded: total, total });
}

/** Alimentos vivos (sin borrar) para la búsqueda y las listas. */
export async function allFoods(): Promise<FoodRow[]> {
  const rows = await db.foods.toArray();
  return rows.filter((f) => f.deletedAt === null);
}

export async function getFood(id: string): Promise<FoodRow | undefined> {
  const row = await db.foods.get(id);
  return row?.deletedAt === null ? row : undefined;
}

/**
 * Marca o desmarca un alimento como favorito (SPEC 6.B).
 *
 * Se encola como cualquier otro cambio: los favoritos son del usuario, no del
 * dispositivo, así que viajan a sus otros móviles.
 */
export async function toggleFavorite(foodId: string): Promise<FoodRow | null> {
  const food = await db.foods.get(foodId);
  if (!food) return null;
  const row: FoodRow = { ...food, favorite: !food.favorite, updatedAt: nowIso() };
  await putAndQueue('foods', row.id, row);
  return row;
}
